import { z } from 'zod';
import { activeStatusSchema, elementSchema, factionSchema } from '../characters/characterSchemas';
import { getItemById } from '../items/items';
import { getEnemySkillById } from './enemySkills';
import ambusherJson from './data/ambusher.json';
import blackKnightJson from './data/black_knight.json';
import boneGuardJson from './data/bone_guard.json';
import demonCommanderJson from './data/demon_commander.json';
import giantSpiderJson from './data/giant_spider.json';

const enemyDropSchema = z
  .object({
    itemId: z.string().min(1),
    chance: z.number().min(0).max(1),
    minQuantity: z.number().int().positive().default(1),
    maxQuantity: z.number().int().positive().default(1),
  })
  .refine((drop) => drop.maxQuantity >= drop.minQuantity, {
    message: 'maxQuantity must be >= minQuantity',
  });

export const enemySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  faction: factionSchema,
  maxHp: z.number().int().positive(),
  accuracy: z.number().min(0).max(1),
  expReward: z.number().int().nonnegative(),
  goldReward: z.number().int().nonnegative().default(0),
  skillIds: z.array(z.string().min(1)).min(1),
  statuses: z.array(activeStatusSchema).default([]),
  drops: z.array(enemyDropSchema).default([]),
  weak: z.array(elementSchema).default([]),
  resist: z.array(elementSchema).default([]),
  immune: z.array(elementSchema).default([]),
});

export type EnemyDef = z.infer<typeof enemySchema>;
export type EnemyDropDef = z.infer<typeof enemyDropSchema>;

export const enemies: EnemyDef[] = z.array(enemySchema).parse([
  boneGuardJson,
  giantSpiderJson,
  blackKnightJson,
  demonCommanderJson,
  ambusherJson,
]);

for (const enemy of enemies) {
  for (const drop of enemy.drops) {
    if (!getItemById(drop.itemId)) {
      throw new Error(`Enemy ${enemy.id} references unknown item ${drop.itemId}`);
    }
  }
  for (const skillId of enemy.skillIds) {
    const skill = getEnemySkillById(skillId);
    if (!skill) {
      throw new Error(`Enemy ${enemy.id} references unknown skill ${skillId}`);
    }
    if (skill.faction !== enemy.faction) {
      throw new Error(`Enemy ${enemy.id} cannot use ${skillId} from faction ${skill.faction}`);
    }
  }
}

export function getEnemyById(id: string): EnemyDef | undefined {
  return enemies.find((enemy) => enemy.id === id);
}

export function getEnemyByName(name: string): EnemyDef | undefined {
  return enemies.find((enemy) => enemy.name === name);
}

export function resolveEnemy(enemyIdOrName: string): EnemyDef {
  return (
    getEnemyById(enemyIdOrName) ??
    getEnemyByName(enemyIdOrName) ?? {
      id: 'unknown',
      name: enemyIdOrName,
      description: 'Một bóng hình đe dọa trong mê cung.',
      faction: 'demon',
      maxHp: 30,
      accuracy: 0.7,
      expReward: 15,
      goldReward: 5,
      skillIds: ['surprise_stab'],
      statuses: [],
      drops: [],
      weak: [],
      resist: [],
      immune: [],
    }
  );
}
